import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '../../assets/logo.svg';

interface MaintenancePageProps {
  message?: string;
  updatedAt?: string;
}

export default function MaintenancePage({ message, updatedAt }: MaintenancePageProps) {
  const { i18n } = useTranslation();
  // Env-specific heading: dev shows restricted notice; prod shows Apple-style message
  const headingText = import.meta.env.PROD ? 'We’ll be right back.' : 'Authorized person only';

  const formattedUpdatedAt = useMemo(() => {
    if (!updatedAt) return null;
    try {
      const d = new Date(updatedAt);
      return d.toLocaleString(i18n.language === 'mm' ? 'my' : 'en', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return updatedAt;
    }
  }, [updatedAt, i18n.language]);

  // Apple-style: clean, centered, generous whitespace, simple typography
  return (
    <div className="min-h-dvh md:min-h-screen bg-white text-black flex items-center justify-center p-6">
      <div className="max-w-xl w-full text-center">
        <div className="flex items-center justify-center mb-6">
          <img src={Logo} alt="EZTopup" className="h-16 w-auto opacity-80" />
        </div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">{headingText}</h1>
        {message && (
          <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">{message}</p>
        )}
        {formattedUpdatedAt && (
          <p className="text-sm text-gray-500">Updated: {formattedUpdatedAt}</p>
        )}
        <div className="mt-10">
          <p className="text-xs text-gray-500">
            Maintenance in progress. Thanks for your patience.
          </p>
        </div>
      </div>
    </div>
  );
}
